import { UIActions } from 'State/UI/UIActions';
import BaseUISystem from '../../BaseUISystem';
import { IGameState } from 'State/Game/IGameState';
import Game from 'Game';
import { track } from 'Utility/Tracking/Track';
import BaseMenu from '../BaseMenu';
import TextComponent from 'Component/Render/Text';
import { RewardType } from 'Utility/Rewards/RewardType';
import LevelHelper from 'Utility/LevelHelper';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import Renderable from 'Component/Renderable';
import Timer from '../../../Timer/Timers/Timer';
import { TransactionType } from 'Utility/Tracking/DataSet/Transaction';
import { AdContexts } from 'Utility/Tracking/DataSet/Ad';
import { GameActions, IEndLevelData, LevelFailReason } from 'State/Game/GameActions';
import { ContextType } from 'State/Game/ContextType';
import { AD_COOLDOWN_TIME, ADS_BEFORE_COOLDOWN, AdsConfig, WATCH_TO_PLAY_LEVEL } from 'Constants/Ads';
import { AdvertisementArea } from 'Utility/SDK/Advertisement';
import { Anim, StartAnimationClipAsync } from 'System/Animation/AnimationHelper';
import { TimerType } from 'System/Timer/TimerTypes';
import DateHelper from 'Utility/DateHelper';
import Interactable from 'Component/Interactable';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import { WinCriteriaType } from 'Constants/WinCriteria';
import SpriteComponent from 'Component/Render/Sprite';
import TransformComponent from 'Component/Transform';
import { ProgressStateHelper } from 'State/Player/Progress/ProgressStateHelper';
import { Easing } from '@tweenjs/tween.js';
import { PlaySound } from 'System/Audio/AudioHelper';
import { applyDefaultButtonAnimation } from '../../UIHelper';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import { sleep } from 'Utility/async';
import { PreBoosterType } from 'State/Card/BoosterType';
import { LevelPopupBooster } from './LevelPopupBooster';
import { trackFtuxOnHide, trackLevelEndShown, trackLevelStartShown } from './LevelPopupTrack';
import { trackShownLevelPopup } from './LevelPopupTrackShown';
import { SideMenu } from './SideMenu';
import Armature from 'Prototype/Armature';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import Point from 'Utility/Point';
import { DragonbonesDefault, LevelCost } from '../../../Animation/Clip';
import { LevelStatus } from 'State/Level/LevelStatus';
import { PreBoosterTutorial } from './PreBoosterTutorial';
import { PopupID } from 'State/UI/PopupID';
import { isIOS, Platform } from 'Utility/Platform';
import { LevelPopupLayout } from 'Layouts/LevelPopup/LevelPopupLayout';
import { EventStateHelper } from 'State/Events/EventStateHelper';
import TimerStateHelper from 'State/Timer/TimerStateHelper';
import { PlayerActions } from 'State/Player/PlayerActions';
import ColorFilter from 'Component/ColorFilter';
import { getLayout, GLayout } from 'Utility/layout/LayoutHelper';
import getEvent = EventStateHelper.getEvent;
import getEventLayout = EventStateHelper.getEventLayout;
import PIXIRenderer from 'System/Render/PIXIRenderer';
import { EventCharacterIDs, isCurrentEvent } from 'Constants/Events/CurrentEvent';

type LevelPopupProps = {
    fail?: boolean;
    levelNumber: number;
    failReason?: LevelFailReason;
    selectedPreBoosters?: PreBoosterType[];
    boughtBooster?: PreBoosterType | null;
    skipAnimations?: boolean;
};

export class LevelPopup extends BaseMenu {
    private adTimer: Timer | null = null;
    private levelNumber: number;
    private alreadyCompleted: boolean = false;
    private layout: GLayout<LevelPopupLayout>;
    private sideMenu: SideMenu;
    private freeLvlArmature: Armature;
    private boosters: LevelPopupBooster[];
    private fail: boolean;
    private failReason: LevelFailReason;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.LEVEL_POPUP, uiSystem, false);
    }

    public onShow(props: LevelPopupProps) {
        super.onShow(props);
        trackShownLevelPopup({ levelNumber: this.levelNumber });

        this.levelNumber = props.levelNumber;
        this.failReason = props.failReason;
        this.fail = Boolean(this.failReason);
        this.fail
            ? trackLevelEndShown({ levelNumber: props.levelNumber })
            : trackLevelStartShown({ levelNumber: props.levelNumber });

        track('tLevelContext', this.levelNumber);
        track('adLevelNum', this.levelNumber);

        const state: IGameState = Game.store.getState();
        const starsAmount: number = ProgressStateHelper.getLevelStars(state, this.levelNumber);
        const levelGoal = WorldDataHelper.getLevelGoal(this.levelNumber);
        const isClearAll = levelGoal[0].type === WinCriteriaType.CLEAR_ALL;

        this.alreadyCompleted = starsAmount > 0;
        this.layout = getEventLayout(state, PopupID.LEVEL_POPUP) ?? getLayout(LevelPopupLayout);
        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);

        this.initSideMenu();
        this.initAdTimer();

        this.freeLvlArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'free_lvl_spend',
            armatureName: 'Armature',
            alpha: 1,
            playOnStart: false,
        });
        const armatureTransform: TransformComponent = this.freeLvlArmature.getComponent(TransformComponent);
        armatureTransform.position.set(-10, -4);
        armatureTransform.scale.set(0.75, 0.75);
        this.freeLvlArmature.getComponent(Renderable).visible = false;
        this.layout.costContainer.addChild(this.freeLvlArmature);

        const { titleText, titleIcon, levelText, playFreeContainer, playContainer, titleContainer } = this.layout;
        const { hardLevelText } = this.layout;
        const { playText, costLabel } = this.layout;
        const levelLabel = Game.locale.getText('Level').replace('[x]', this.levelNumber.toString());
        const hardLevelLabel = `${Game.locale.getText('HardLevel')}: ${this.levelNumber.toString()}`;
        const title = this.fail
            ? `${Game.locale.getText('OhNo')} ${Game.locale.getText('TryAgain')}`
            : Game.locale.getText(isClearAll ? 'ClearAllCards' : 'FindGoldCards');

        const tntExploded = this.fail && this.failReason === LevelFailReason.TNT_EXPLODED;

        titleText.getComponent(TextComponent).text = title;
        titleText.getComponent(TextComponent).style.wordWrap = !this.fail;
        titleText.getComponent(Renderable).visible = !tntExploded;

        titleIcon.getComponent(SpriteComponent).texture = isClearAll ? 'goal_icon' : 'gold_cards_goal_icon';
        titleIcon.getComponent(Renderable).visible = !tntExploded;

        costLabel.getComponent(TextComponent).text = `${Game.locale.getText('Cost')}:`;
        titleContainer.getComponent(TransformComponent).y = this.fail ? -120 : -100;
        levelText.getComponent(TextComponent).text = levelLabel;
        hardLevelText.getComponent(TextComponent).text = hardLevelLabel;
        playText.getComponent(TextComponent).text = Game.locale.getText(this.fail ? 'Replay' : 'Play');

        const titleTextTransform = titleText.getComponent(TransformComponent);
        const titleIconTransform = titleIcon.getComponent(TransformComponent);
        titleIconTransform.x = -titleTextTransform.width / 2 - 10;
        titleTextTransform.x = titleIconTransform.width / 2 + 10;

        if (this.levelNumber <= WATCH_TO_PLAY_LEVEL) {
            playFreeContainer.getComponent(Renderable).visible = false;
            playContainer.getComponent(TransformComponent).x = 0;
        }

        const { playFreeButton, playButton, closeButton, character } = this.layout;
        [playFreeButton, playButton, closeButton].forEach(o => applyDefaultButtonAnimation(o));

        playFreeContainer.addComponent(ColorFilter);

        playFreeButton.on(GameObjectEvent.SELECTED, this.handleFreePlayClick);
        playButton.on(GameObjectEvent.SELECTED, this.handlePlayClick);
        closeButton.once(GameObjectEvent.SELECTED, this.handleCloseClick);

        character.addComponent(RelativePositionComponent).y = 0;

        const isEventActive = isCurrentEvent(getEvent(state, PopupID.LEVEL_POPUP));

        if (this.fail) {
            character.getComponent(SpriteComponent).texture = isEventActive
                ? tntExploded
                    ? EventCharacterIDs.levelFailTNT
                    : EventCharacterIDs.levelFail
                : tntExploded
                    ? 'UI_TNT_Fail_Character'
                    : 'character_failed_1';
            character.getComponent(TransformComponent).x = tntExploded ? -515 : -450;
        } else {
            character.getComponent(SpriteComponent).texture = isEventActive
                ? EventCharacterIDs.prelevel
                : 'character_idle_1';
            character.getComponent(TransformComponent).x = -515;
        }

        this.boosters = [PreBoosterType.REMOVE_THREE, PreBoosterType.JOKER, PreBoosterType.CANISTER].map((type, i) => {
            const booster = new LevelPopupBooster({ type, justBought: props.boughtBooster === type });
            booster.onClick.addListener(() => this.handleBoosterClick(booster));
            booster.root.getComponent(TransformComponent).x = (i - 1) * 200;
            this.layout.boostersContainer.addChild(booster.root);
            booster.isSelected = props.selectedPreBoosters?.includes(type) ?? false;
            booster.update();

            return booster;
        });

        this.layout.tntFailContainer.getComponent(Renderable).visible = tntExploded;

        this.layout.failContainers.forEach((container: any) => {
            container.getComponent(Renderable).visible = this.fail;
        });

        if (WorldDataHelper.isHardLevel(this.levelNumber)) {
            this.layout.hardLevelNumberBg.getComponent(Renderable).visible = true;
            this.layout.levelText.getComponent(Renderable).visible = false;

            this.layout.starPlaces.forEach((starPlace: any, i: number) => {
                starPlace.getComponent(Renderable).visible = !isEventActive;
                starPlace.getComponent(TransformComponent).x += [1, 0, -1][i];
                starPlace.getComponent(SpriteComponent).texture = 'star_base_hard';
            });

            this.layout.stars.forEach((star: any, i: number) => {
                star.getComponent(SpriteComponent).texture = 'star_hard';
                if (isEventActive) {
                    const starTransform = star.getComponent(TransformComponent);
                    starTransform.y += [5, 5, 4][i];
                    starTransform.x += [13, 11, 11][i];
                }
            });

            if (isEventActive) {
                // @ts-ignore
                this.layout.levelTextBg?.getComponent(Renderable).update({ visible: false });
                const hardLvlBgTransform = this.layout.hardLevelNumberBg?.getComponent(TransformComponent);
                if (hardLvlBgTransform) {
                    hardLvlBgTransform.x += 10;
                    hardLvlBgTransform.y += 18;
                    hardLvlBgTransform.scale.set(1.05, 1.05);
                }
            }

            this.layout.headerBg.getComponent(SpriteComponent).texture = isEventActive
                ? EventCharacterIDs.hardHeader
                : 'header_hard';
            this.layout.headerBg.getComponent(TransformComponent).y += 12;
            this.layout.tntHeaderBg.getComponent(SpriteComponent).texture = isEventActive
                ? EventCharacterIDs.hardHeader
                : 'header_hard';
            this.layout.tntHeaderBg.getComponent(TransformComponent).y += 12;

            this.layout.closeButton.getComponent(SpriteComponent).texture = 'x_button_hard';
            this.layout.bg.getComponent(SpriteComponent).texture = 'popup_background_hard';
        }

        this.updateStars(starsAmount, this.fail || props.skipAnimations);
        this.updateCost(state);
        this.updateAdButton(state);

        const isPlinkoLevel = WorldDataHelper.isPlinkoLevel(this.levelNumber);
        const levelData = ProgressStateHelper.getLevelData(state, this.levelNumber);
        this.layout.plinkoSubLayout.root.getComponent(Renderable).visible =
            isPlinkoLevel && !this.fail && (levelData?.attempts ?? 0) < 1;
        const { plinkoCard, infoDialog } = this.layout.plinkoSubLayout;
        if (isPlinkoLevel) {
            const infoDialogSprite = infoDialog.getComponent(SpriteComponent);
            infoDialogSprite.alpha = 0;
            plinkoCard.on(GameObjectEvent.SELECTED, () => {
                if (infoDialogSprite.alpha === 0) {
                    Anim(infoDialog, {
                        alpha: 1,
                        duration: 300,
                    }).then(() => {
                        Anim(infoDialog, {
                            alpha: 0,
                            duration: 300,
                            delay: 15000,
                        });
                    });
                } else if (infoDialogSprite.alpha === 1) {
                    Anim(infoDialog, {
                        alpha: 0,
                        duration: 300,
                    });
                }
            });
        }

        // Check if timer should be reset immediately.
        this.onTimerUpdate();
    }

    protected toggleInteractables(value: boolean): void {
        // handles interactables itself
    }

    protected onShown(params?: any): void {
        super.onShown(params);
        if (!this.fail) {
            PreBoosterTutorial.tryToShowBoostersTutorial({ boosters: this.boosters, parent: this.layout.root });
        }
    }

    public onDestroy(): void {
        this.adTimer?.stop();
        super.onDestroy();
    }

    private setInputEnabled(value: boolean): void {
        this.layout.root.container.interactiveChildren = value;
    }

    private updateCost(state: IGameState): void {
        const hasFreePlay = state.playerState.inventory[RewardType.FREE_PLAY] > 0;
        const cost = LevelHelper.getLevelEntryCost(this.levelNumber, this.alreadyCompleted);
        const priceTextComponent = this.layout.priceText.getComponent(TextComponent);

        if (hasFreePlay && cost > 0) {
            priceTextComponent.text = `x${state.playerState.inventory[RewardType.FREE_PLAY]}`;
            this.freeLvlArmature.getComponent(Renderable).visible = true;
            this.freeLvlArmature.container.animation.gotoAndPlayByProgress('idle');
            this.layout.coin.getComponent(Renderable).visible = false;
        } else {
            track('tCoinsLevelEntry', cost);
            priceTextComponent.text = cost === 0 ? Game.locale.getText('Free') : cost.toString();
        }
    }

    private async playLevel(): Promise<void> {
        this.setInputEnabled(false);
        track('transactionType', TransactionType.COIN_PURCHASE);

        if (this.fail) {
            Game.sdk.gameTracking(FBTrackingEvent.TapOnReplay);
            trackFtuxOnHide({ levelNumber: this.levelNumber });
        }

        const restart = this.fail;
        const state: IGameState = Game.store.getState();
        const entryCost = LevelHelper.getLevelEntryCost(this.levelNumber, this.alreadyCompleted);

        const loadLevel = () => {
            console.log(this.getSelectedPreBoosters());
            if (restart) {
                GameActions.endLevel({
                    status: LevelStatus.RESTARTING,
                    firstComplete: false,
                    preBoosters: this.getSelectedPreBoosters(),
                });
            } else {
                GameActions.loadLevel({
                    levelNumber: this.levelNumber,
                    preBoosters: this.getSelectedPreBoosters(),
                    restart,
                });
            }
        };

        if (entryCost > state.playerState.inventory.coins) {
            loadLevel();
            return;
        }

        if (state.playerState.inventory[RewardType.FREE_PLAY] > 0) {
            this.layout.priceText.getComponent(TextComponent).text = `x${state.playerState.inventory[
                RewardType.FREE_PLAY
            ] - 1}`;

            await StartAnimationClipAsync(
                new DragonbonesDefault(this.freeLvlArmature, {
                    animationName: 'spend',
                    progress: 0,
                    playTimes: 1,
                })
            );

            loadLevel();
            return;
        }

        await StartAnimationClipAsync(
            new LevelCost(this.layout.priceText, {
                levelNumber: this.levelNumber,
                alreadyCompleted: this.alreadyCompleted,
            })
        );

        loadLevel();
    }

    private playLevelFree(): void {
        this.setInputEnabled(false);
        track('transactionType', TransactionType.AD);
        track('adContext', AdContexts.FREE_PLAY);

        GameActions.setContext(ContextType.DISPLAY_ADVERTISEMENT, {
            placement: AdsConfig.getRewardedWatchToPlay(Game.config),
            payload: {
                g_gameArea: AdvertisementArea.WATCH_TO_PLAY,
                reward: {
                    type: RewardType.FREE_PLAY,
                    value: 1,
                },
                levelPayload: {
                    levelNumber: this.levelNumber,
                    preBoosters: this.getSelectedPreBoosters(),
                },
            },
        });
    }

    private updateAdButton(state: IGameState): void {
        const { adsAmount, playFreeText, playFreeButton, playFreeContainer } = this.layout;

        // Ignore ad watch counts on iOS.
        if (isIOS()) {
            adsAmount.getComponent(Renderable).visible = false;
            playFreeContainer.getComponent(ColorFilter).saturation = 1;
            playFreeButton.getComponent(Interactable).enabled = true;
            playFreeText.getComponent(TextComponent).text = Game.locale.getText('PlayFree');

            return;
        }

        const remainingAdCount: number = state.playerState.progress.adsWatched
            ? ADS_BEFORE_COOLDOWN - state.playerState.progress.adsWatched
            : ADS_BEFORE_COOLDOWN;

        // If no ad timer is set then assume it is complete. Somehow the ad timer and `adsWatched` values can become
        // out of sync. This condition patches that issue.
        const noAdTimer = !state.playerState.timers.has(TimerType.AD_COOLDOWN);

        // Check if the ad cooldown is complete (or null). This prevents negative time differences (showing -1:-1:-1 durations).
        const adComplete = noAdTimer
            ? false
            : TimerStateHelper.isTimerComplete(state, TimerType.AD_COOLDOWN, AD_COOLDOWN_TIME);

        if (remainingAdCount > 0 || noAdTimer || adComplete) {
            adsAmount.getComponent(Renderable).visible = true;
            adsAmount.getComponent(TextComponent).text =""// `${remainingAdCount}x ${Game.locale.getText('Available')}`;
            playFreeContainer.getComponent(ColorFilter).saturation = 1;
            playFreeButton.getComponent(Interactable).enabled = true;
            playFreeText.getComponent(TextComponent).text = Game.locale.getText('PlayFree');

            return;
        }

        // Currently on cooldown.
        const completeTime = new Date(state.playerState.timers.get(TimerType.AD_COOLDOWN).getTime() + AD_COOLDOWN_TIME);

        playFreeContainer.getComponent(ColorFilter).saturation = 0;
        adsAmount.getComponent(Renderable).visible = false;
        playFreeButton.getComponent(Interactable).enabled = false;
        playFreeText.getComponent(TextComponent).text = DateHelper.timeTo(DateHelper.now, completeTime, 'hh:mm:ss');

        return;
    }

    private updateStars(starsAmount: number, skipAnimations: boolean): void {
        const sounds = ['3-stars-1', '3-stars-2', '3-stars-3'];

        this.layout.stars.map(async (star: any, i: number) => {
            const visible = i < starsAmount;
            star.getComponent(Renderable).visible = visible;

            if (visible && !skipAnimations) {
                const transform = star.getComponent(TransformComponent);
                transform.scale.set(0);

                await sleep(500 + i * 350);
                Anim(star, { scaleX: 1, scaleY: 1, duration: 450, easing: Easing.Back.Out });
                await sleep(300);
                PlaySound(sounds[i]);
            }
        });
    }

    private handleFreePlayClick = (): void => {
        this.playLevelFree();
    };

    private handlePlayClick = (): void => {
        Game.sdk.gameTracking(FBTrackingEvent.TapOnPlay);
        this.playLevel();
    };

    private handleCloseClick = (): void => {
        this.setInputEnabled(false);
        track('tLevelContext', undefined);
        track('adLevelNum', undefined);
        track('tCoinsLevelEntry', undefined);

        if (this.fail) {
            trackFtuxOnHide({ levelNumber: this.levelNumber });
            const levelEndPayload: IEndLevelData = {
                status: LevelStatus.EXITING,
                firstComplete: false,
                failReason: this.failReason,
            };

            GameActions.endLevel(levelEndPayload);

            return;
        }

        UIActions.hidePopup(this.name);
    };

    private readonly handleBoosterClick = (booster: LevelPopupBooster): void => {
        PlaySound('button-press');
        if (booster.getAmount() === 0) {
            this.openBuyBoosterPopup(booster.type);
        } else {
            booster.isSelected = !booster.isSelected;
            booster.update();
        }
    };

    private openBuyBoosterPopup(type: PreBoosterType): void {
        UIActions.hidePopup(this.name);

        const selectedBoosters = this.getSelectedPreBoosters();

        UIActions.showPopup(PopupID.BUY_BOOSTER_POPUP, {
            type,
            onClose: (bought: boolean): void => {
                bought && selectedBoosters.push(type);

                const props: LevelPopupProps = {
                    levelNumber: this.levelNumber,
                    failReason: this.failReason,
                    selectedPreBoosters: selectedBoosters,
                    boughtBooster: bought ? type : null,
                    skipAnimations: true,
                };

                if (Game.store.getState().UIState.popupID) {
                    UIActions.queuePopup(PopupID.LEVEL_POPUP, props);
                } else {
                    UIActions.showPopup(PopupID.LEVEL_POPUP, props);
                }
            },
        });
    }

    private getSelectedPreBoosters(): PreBoosterType[] {
        return this.boosters.filter(b => b.isSelected).map(b => b.type);
    }

    private initSideMenu() {
        if (Game.config.platform === Platform.ANDROID || Game.config.platform === Platform.IOS) return;
        this.sideMenu = new SideMenu({ levelNumber: this.levelNumber });
        this.layout.sideContainer.addChild(this.sideMenu.root);
    }

    private onTimerUpdate() {
        const state = Game.store.getState();
        if (
            state.playerState.progress.adsWatched > 0 &&
            TimerStateHelper.isTimerComplete(state, TimerType.AD_COOLDOWN, AD_COOLDOWN_TIME, false)
        ) {
            PlayerActions.completeAdCooldownTimer();
        } else {
            this.updateAdButton(state);
        }
    }

    private initAdTimer(): void {
        if (!isIOS()) {
            this.adTimer = new Timer(false);
            this.adTimer.onUpdate = this.onTimerUpdate.bind(this);
            this.adTimer.start();
        }
    }

    protected resize(screen: { CANVAS_WIDTH: number; CANVAS_HEIGHT: number }): void {
        super.resize(screen);
        if (this.element) {
            this.element.container.resize(PIXIRenderer.renderer.width, PIXIRenderer.renderer.height);
        }
    }
}
